import { ID, Task, TaskType, Timestamp } from '@griply/types';
import { canOpenHabitOptions } from '@griply/utils';
import React, { forwardRef, useMemo, useState } from 'react';
import {
  DateSubMenu,
  DeadlineDeadlineSubMenu,
  LinkedItemSubMenu,
  PrioritySubMenu,
  RemindersSubMenu,
  TagsSubMenu,
} from 'shared/components/connected/task-card-multi-select-context-menu';
import { TimeslotContextMenu } from 'shared/components/connected/timeslot-context-menu';
import { Placement, PopupMenu } from 'shared/components/ui/popup-menu';
import { useToday } from 'shared/contexts/today';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { InteractionType } from 'shared/types/data-type';

import { CompleteList } from './complete-list';
import { EditList } from './edit-list';
import { LifecycleList } from './lifecycle-list';
import { RestoreList } from './restore-list';
import { TimeslotList } from './timeslot-list';
import { SubMenu } from './types';

export type TaskCardContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref' | 'onOutside'
> & {
  task: Task;
  onClose: () => void;
  nameRef?: React.RefObject<HTMLInputElement>;
  hideMeta?: boolean;
  hideEdit?: boolean;
  occurrenceId?: ID;
  timeslotDate?: Timestamp;
  initialActiveSubMenu?: SubMenu;
  onAction?: () => void;
  onUpdate?: (task: Task, updates: any) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
};

export const TaskCardContextMenu = forwardRef<
  HTMLDivElement,
  TaskCardContextMenuProps
>(
  (
    {
      task,
      onClose,
      referenceElement,
      location,
      nameRef,
      hideEdit,
      occurrenceId,
      timeslotDate,
      initialActiveSubMenu,
      onAction,
      onUpdate,
      onDelete,
      onDuplicate,
    },
    ref,
  ) => {
    const [openSubMenu, setOpenSubMenu] = useState<SubMenu | undefined>(
      initialActiveSubMenu,
    );
    const today = useToday();

    const isSubtask = !!task.parentIds?.length;
    const isHabit = [TaskType.Habit, TaskType.Repeating].includes(task.type);
    const isFutureDate = useMemo(
      () =>
        !!timeslotDate &&
        !canOpenHabitOptions(task, { today, displayDate: timeslotDate }),
      [task, timeslotDate, today],
    );

    const onChangeTimeslot = () => setOpenSubMenu(SubMenu.Timeslot);

    useKeysDown(
      ['KeyP'],
      (e) => {
        e.preventDefault();
        setOpenSubMenu(SubMenu.Priority);
      },
      { ignoreInputFields: true },
    );

    useKeysDown(
      ['KeyR'],
      (e) => {
        e.preventDefault();
        setOpenSubMenu(SubMenu.ReminderTime);
      },
      { ignoreInputFields: true },
    );

    useKeysDown(
      ['KeyG'],
      (e) => {
        e.preventDefault();
        setOpenSubMenu(SubMenu.LinkedItem);
      },
      { ignoreInputFields: true },
    );

    useKeysDown(
      ['KeyD'],
      (e) => {
        if (e.shiftKey) {
          e.preventDefault();
          setOpenSubMenu(SubMenu.DeadlineDeadline);
          return;
        }
        if (e.metaKey || e.ctrlKey || e.altKey) {
          return;
        }
        e.preventDefault();
        setOpenSubMenu(SubMenu.Date);
      },
      { ignoreInputFields: true, otherKeysInCombinationAllowed: true },
    );

    useKeysDown(
      ['KeyT'],
      (e) => {
        e.preventDefault();
        setOpenSubMenu(SubMenu.Tags);
      },
      { ignoreInputFields: true },
    );

    useKeysDown(
      ['Escape'],
      (e) => {
        e.stopImmediatePropagation();
        onClose();
      },
      { ignoreInputFields: true },
    );

    const menuProps = {
      ref,
      location,
      referenceElement,
      onOutside: onClose,
      interactionType: InteractionType.Task,
      position: 'bottom-start' as Placement,
    };

    return (
      <>
        {!openSubMenu && (
          <PopupMenu {...menuProps}>
            {isSubtask && (
              <LifecycleList
                task={task}
                onClose={onClose}
                onAction={onAction}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
              />
            )}
            {!isSubtask &&
              (isHabit && isFutureDate && !task.deletedAt ? (
                <>
                  <EditList
                    task={task}
                    onClose={onClose}
                    nameRef={nameRef}
                    openSubMenu={setOpenSubMenu}
                    onAction={onAction}
                    hideEdit={hideEdit}
                  />
                  <TimeslotList
                    task={task}
                    date={timeslotDate!}
                    id={occurrenceId}
                    onClose={onClose}
                    onChangeTimeslot={onChangeTimeslot}
                    onAction={onAction}
                  />
                  <LifecycleList
                    task={task}
                    onClose={onClose}
                    onAction={onAction}
                    onDelete={onDelete}
                    onDuplicate={onDuplicate}
                  />
                </>
              ) : (
                <>
                  {task.deletedAt && (
                    <RestoreList task={task} onClose={onClose} />
                  )}
                  <EditList
                    task={task}
                    onClose={onClose}
                    nameRef={nameRef}
                    openSubMenu={setOpenSubMenu}
                    onAction={onAction}
                    hideEdit={hideEdit}
                  />
                  {isHabit && (
                    <CompleteList
                      task={task}
                      onClose={onClose}
                      date={timeslotDate}
                    />
                  )}
                  {/* TODO: this menu only needs to be shown on Today and in Upcoming

                  {!!timeslotDate && (
                    <TimeslotList
                      task={task}
                      date={timeslotDate}
                      id={occurrenceId}
                      onClose={onClose}
                      onChangeTimeslot={onChangeTimeslot}
                    />
                  )} */}
                  {!task.deletedAt && (
                    <LifecycleList
                      task={task}
                      onClose={onClose}
                      onAction={onAction}
                      onDelete={onDelete}
                      onDuplicate={onDuplicate}
                    />
                  )}
                  {/*{isHabit && <HabitList onClose={onClose} task={task} />}*/}
                </>
              ))}
          </PopupMenu>
        )}

        {openSubMenu === SubMenu.Priority && (
          <PrioritySubMenu
            tasks={[task]}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}

        {openSubMenu === SubMenu.LinkedItem && (
          <LinkedItemSubMenu
            tasks={[task]}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}

        {openSubMenu === SubMenu.DeadlineDeadline && (
          <DeadlineDeadlineSubMenu
            tasks={[task]}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}

        {openSubMenu === SubMenu.ReminderTime && (
          <RemindersSubMenu
            tasks={[task]}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}

        {openSubMenu === SubMenu.Date && (
          <DateSubMenu
            tasks={[task]}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}

        {openSubMenu === SubMenu.Tags && (
          <TagsSubMenu tasks={[task]} onClose={onClose} {...menuProps} />
        )}

        {openSubMenu === SubMenu.Timeslot && (
          <TimeslotContextMenu
            {...menuProps}
            task={task}
            onClose={onClose}
            representedDay={timeslotDate!}
          />
        )}
      </>
    );
  },
);
